/**
 * Pure helper for Word `Range.compareLocationWith` results (`Word.LocationRelation`).
 *
 * Table-cell apply cannot rely on `paragraph.getRange('Whole').search()` — Mac Word returns 0 hits
 * even when the text is in the cell. The fallback searches `document.body` and keeps only hits that
 * sit inside the target paragraph. This file is the decision rule for that filter (unit-tested).
 *
 * `compareLocationWith` is invoked on the *search hit* with the paragraph range as the argument, so
 * "Inside*" means the hit is inside the paragraph.
 */

const HIT_INSIDE_PARAGRAPH = new Set([
  'inside',
  'insidestart',
  'insideend',
  'equal',
  'overlapsbefore',
  'overlapsafter',
]);

/**
 * True when a body-search hit belongs to the target paragraph.
 *
 * Overlaps* is accepted because a table cell's Whole range includes the end-of-cell marker, so a
 * hit of the visible text can overlap the paragraph range rather than sit strictly Inside.
 */
export function isSearchHitInsideParagraph(location: string | null | undefined): boolean {
  if (!location) return false;
  return HIT_INSIDE_PARAGRAPH.has(location.replace(/\s+/g, '').toLowerCase());
}

function foldHitText(text: string): string {
  return (text || '').replace(/[\r\n\u0007]+/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Word RTL `Range.search` can return a range that starts at the needle but
 * extends through the rest of the paragraph. Deleting that hit strikes the
 * tail — the "only part applied" / green-strikethrough-the-rest bug.
 */
export function isFaithfulSearchHit(searchValue: string, hitText: string): boolean {
  const wanted = foldHitText(searchValue);
  const got = foldHitText(hitText);
  if (!wanted) return false;
  if (got === wanted) return true;
  // Paragraph / cell mark may ride along on Whole-range hits.
  if (got.length <= wanted.length + 2 && got.startsWith(wanted)) return true;
  return false;
}
